import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltTile } from '@cae/cobalt-react/components/tile';
import { CobaltIconDownloadOutline } from '@cae/cobalt-react/icons/outline/CobaltIconDownloadOutline';
import { useDownloadCreditMemo } from '@features/invoices/hooks/use-download-credit-memo/useDownloadCreditMemo';
import { formatAmount } from '@features/invoices/utils/invoices.utils';
import { DataValue } from '@shared/components/data-value/DataValue';
import { FEATURE_FLAGS } from '@shared/constants/featureFlags.constants';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';

import type { CreditMemoCardMobileProps } from './CreditMemoCardMobile.types';

import styles from './CreditMemoCardMobile.module.css';

const CreditMemoCardMobile = React.memo(
  ({
    credit,
    canDownload,
  }: Readonly<CreditMemoCardMobileProps>): React.ReactElement => {
    const flags = useFlags();
    const creditMemoId = (credit.id ?? '').trim();
    const creditMemoNumber = (credit.creditMemoNumber ?? '').trim();
    const isDownloadable =
      flags[FEATURE_FLAGS.displayCreditMemoDownload] === true &&
      credit.isDownloadAvailable &&
      canDownload &&
      creditMemoId !== '' &&
      creditMemoNumber !== '';
    const { handleDownload, isDownloading } = useDownloadCreditMemo({
      creditMemoId,
      creditMemoNumber,
    });

    const handleClick = React.useCallback((): void => {
      void handleDownload();
    }, [handleDownload]);

    return (
      <CobaltTile
        className={`elevation-03-raised ${styles.creditCard}`}
        data-testid="invoice-credit-row"
      >
        <div className={styles.cardContent}>
          <p className={`${styles.cardTitle} heading-01`}>
            Transaction ID <DataValue value={credit.creditMemoNumber} />
          </p>
          <dl className={styles.cardBody}>
            <div className={styles.cardRow}>
              <dt className={`body-01 ${styles.cardLabel}`}>Applied on</dt>
              <dd className={styles.cardValue}>
                <DataValue value={formatDateShort(credit.appliedOn)} />
              </dd>
            </div>
            <div className={styles.cardRow}>
              <dt className={`body-01 ${styles.cardLabel}`}>Amount</dt>
              <dd className={styles.cardValue}>
                <DataValue
                  value={formatAmount(credit.amount, credit.currency)}
                />
              </dd>
            </div>
          </dl>
          {isDownloadable && (
            <CobaltButton
              variant="ghost"
              className={styles.downloadButton}
              pending={isDownloading}
              aria-label={`Download credit memo ${creditMemoNumber}`}
              onClick={handleClick}
            >
              Download
              <CobaltIconDownloadOutline size="small" aria-hidden="true" />
            </CobaltButton>
          )}
        </div>
      </CobaltTile>
    );
  }
);

CreditMemoCardMobile.displayName = 'CreditMemoCardMobile';
export { CreditMemoCardMobile };
